// src/pages/OptIn.jsx
import React, { useRef, useState, useEffect } from "react";
import ReCAPTCHA from "react-google-recaptcha";

const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const SITE_KEY_V2 = process.env.REACT_APP_RECAPTCHA_SITE_KEY_V2;

const SUPPORT_PHONE_E164 =
  process.env.REACT_APP_SUPPORT_PHONE_E164 || "+1-877-705-1818";

const RAW_DISPLAY =
  process.env.REACT_APP_SUPPORT_PHONE_DISPLAY || "1-877-705-1818";
const SUPPORT_PHONE_DISPLAY_NB = RAW_DISPLAY
  .replace(/-/g, "\u2011")
  .replace(/\s/g, "\u00A0");

const OptIn = () => {
  const [form, setForm] = useState({
    fullName: "",
    mobile: "",
    email: "",
    consentSms: false,
    consentPolicies: false,
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState(null);

  const recaptchaRef = useRef(null);

  // 📱 detect phone/tablet to match Signup/Login/Contact small-screen tweaks
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1024px)");
    const apply = () => setIsSmallScreen(mq.matches);
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);

  const onChange = (e) => {
    const { name, type, checked, value } = e.target;
    setForm((s) => ({ ...s, [name]: type === "checkbox" ? checked : value }));
  };

  const validate = () => {
    const errs = {};
    const fullName = (form.fullName || "").trim();
    const mobile = (form.mobile || "").trim();
    const email = (form.email || "").trim();

    if (!fullName) errs.fullName = "Full name is required.";
    if (!mobile) errs.mobile = "Mobile number is required.";

    if (email) {
      const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      if (!ok) errs.email = "Enter a valid email.";
    }

    if (!form.consentSms) errs.consentSms = "You must consent to receive SMS.";
    if (!form.consentPolicies)
      errs.consentPolicies = "Please agree to Terms and Privacy Policy.";

    setFieldErrors(errs);
    if (Object.keys(errs).length) {
      setNotice({ type: "error", msg: "Please fix the highlighted fields." });
      return false;
    }
    return true;
  };

  const getFreshToken = async () => {
    if (!recaptchaRef.current) throw new Error("captcha-not-ready");
    const EXEC_TIMEOUT_MS = 15000;

    const execPromise = recaptchaRef.current.executeAsync();
    const timeoutPromise = new Promise((_, rej) =>
      setTimeout(() => rej(new Error("captcha-timeout")), EXEC_TIMEOUT_MS)
    );

    try {
      const token = await Promise.race([execPromise, timeoutPromise]);
      return token;
    } finally {
      try {
        recaptchaRef.current.reset();
      } catch {}
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setNotice(null);

    if (!SITE_KEY_V2) {
      setNotice({ type: "error", msg: "reCAPTCHA not configured." });
      return;
    }
    if (!BACKEND) {
      setNotice({ type: "error", msg: "Backend URL not configured." });
      return;
    }
    if (!validate()) return;

    try {
      setSubmitting(true);

      const token = await getFreshToken();

      const res = await fetch(`${BACKEND}/forms/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "omit",
        body: JSON.stringify({
          type: "optin",
          fullName: form.fullName,
          mobile: form.mobile,
          email: form.email,
          consentSms: !!form.consentSms,
          consentPolicies: !!form.consentPolicies,
          recaptchaToken: token,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        const reason = data?.reason || data?.error || "send-failed";
        throw new Error(reason);
      }

      setNotice({ type: "ok", msg: "📩 Thanks! Your message was sent." });
      setForm({
        fullName: "",
        mobile: "",
        email: "",
        consentSms: false,
        consentPolicies: false,
      });
      setFieldErrors({});
    } catch (err) {
      const em = String(err?.message || "");
      const msg =
        em.includes("captcha") || em.includes("Timeout")
          ? "reCAPTCHA expired or failed. Please try again."
          : `Could not submit (${em}).`;
      setNotice({ type: "error", msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div style={{ ...styles.outerWrap, ...(isSmallScreen ? styles.outerWrapCompact : null) }}>
        <div style={{ ...styles.card, ...(isSmallScreen ? styles.cardWide : null) }}>
          <h2 style={styles.title}>SMS Opt-In</h2>

          <p style={styles.subtitle}>
            You agree to receive <b>transactional messages</b> only (e.g., account &amp; document
            updates, security alerts, and appointment reminders). <b>No marketing texts.</b>
          </p>

          <form onSubmit={handleSubmit} style={styles.form} noValidate>
            <div style={styles.inputWrap}>
              <input
                name="fullName"
                placeholder="Your Full Name *"
                value={form.fullName}
                onChange={onChange}
                style={{
                  ...styles.input,
                  borderColor: fieldErrors.fullName ? "#b00020" : styles.inputBorderColor,
                }}
                required
                disabled={submitting}
                aria-invalid={!!fieldErrors.fullName}
              />
              {fieldErrors.fullName && (
                <div style={styles.errorText}>{fieldErrors.fullName}</div>
              )}
            </div>

            <div style={styles.inputWrap}>
              <input
                name="mobile"
                placeholder="Mobile Number *"
                value={form.mobile}
                onChange={onChange}
                style={{
                  ...styles.input,
                  borderColor: fieldErrors.mobile ? "#b00020" : styles.inputBorderColor,
                }}
                required
                disabled={submitting}
                aria-invalid={!!fieldErrors.mobile}
              />
              {fieldErrors.mobile && (
                <div style={styles.errorText}>{fieldErrors.mobile}</div>
              )}
            </div>

            <div style={styles.inputWrap}>
              <input
                name="email"
                type="email"
                placeholder="Email (optional)"
                value={form.email}
                onChange={onChange}
                style={{
                  ...styles.input,
                  borderColor: fieldErrors.email ? "#b00020" : styles.inputBorderColor,
                }}
                disabled={submitting}
                aria-invalid={!!fieldErrors.email}
              />
              {fieldErrors.email && (
                <div style={styles.errorText}>{fieldErrors.email}</div>
              )}
            </div>

            <label style={styles.checkRow}>
              <input
                type="checkbox"
                name="consentSms"
                checked={form.consentSms}
                onChange={onChange}
                style={styles.checkbox}
                required
                disabled={submitting}
                aria-invalid={!!fieldErrors.consentSms}
              />
              <span>
                I agree to receive <b>transactional SMS</b> related to my account/documents.
              </span>
            </label>
            {fieldErrors.consentSms && (
              <div style={{ ...styles.errorText, marginTop: -8 }}>
                {fieldErrors.consentSms}
              </div>
            )}

            <label style={styles.checkRow}>
              <input
                type="checkbox"
                name="consentPolicies"
                checked={form.consentPolicies}
                onChange={onChange}
                style={styles.checkbox}
                required
                disabled={submitting}
                aria-invalid={!!fieldErrors.consentPolicies}
              />
              <span>
                I agree to the{" "}
                <a href="/terms" target="_blank" rel="noreferrer">
                  Terms
                </a>{" "}
                and{" "}
                <a href="/privacy" target="_blank" rel="noreferrer">
                  Privacy Policy
                </a>
                .
              </span>
            </label>
            {fieldErrors.consentPolicies && (
              <div style={{ ...styles.errorText, marginTop: -8 }}>
                {fieldErrors.consentPolicies}
              </div>
            )}

            <div style={{ height: 0, overflow: "hidden" }}>
              <ReCAPTCHA ref={recaptchaRef} sitekey={SITE_KEY_V2} size="invisible" />
            </div>

            <button type="submit" style={styles.button} disabled={submitting}>
              {submitting ? "Sending…" : "Send"}
            </button>

            <p style={styles.disclosure}>
              Reply <b>STOP</b> to cancel, <b>HELP</b> for help. Msg &amp; data rates may apply.{" "}
              Support:{" "}
              <a href="mailto:support@asklawly.com">support@asklawly.com</a>,{" "}
              <a style={styles.nowrap} href={`tel:${SUPPORT_PHONE_E164}`}>
                {SUPPORT_PHONE_DISPLAY_NB}
              </a>
              .
            </p>

            {notice && (
              <p
                style={{
                  marginTop: 10,
                  color: notice.type === "ok" ? "#0a7c2f" : "#b00020",
                  fontSize: 14,
                  textAlign: "center",
                }}
                aria-live="polite"
              >
                {notice.msg}
              </p>
            )}
          </form>
        </div>
      </div>
    </>
  );
};

const styles = {
  // Matches other forms
  outerWrap: {
    maxWidth: 960,
    margin: "24px auto",
    padding: "0 16px 40px",
  },
  // 📱 small screens: slimmer side padding
  outerWrapCompact: {
    padding: "0 8px 40px",
  },
  // Card metrics
  card: {
    maxWidth: 440,
    margin: "0 auto",
    padding: 28,
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    display: "flex",
    flexDirection: "column",
    fontFamily: "sans-serif",
  },
  // 📱 allow wider card on phones/tablets
  cardWide: {
    maxWidth: 520,
  },
  title: {
    textAlign: "center",
    fontSize: 26,
    fontWeight: "bold",
    marginBottom: 20,
    color: "#111827",
  },
  subtitle: {
    color: "#475569",
    fontSize: 13,
    margin: "0 0 10px",
    textAlign: "center",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
    alignItems: "stretch",
  },
  inputWrap: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
  },
  inputBorderColor: "#ccc",
  input: {
    padding: 12,
    fontSize: 16,
    border: "1px solid #ccc",
    borderRadius: 10,
    outline: "none",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    width: "100%",
    boxSizing: "border-box",
  },
  checkRow: {
    display: "flex",
    gap: 10,
    alignItems: "flex-start",
  },
  checkbox: { marginTop: 4 },
  button: {
    padding: 12,
    fontSize: 16,
    backgroundColor: "#1E2A78",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    width: "100%",
    fontWeight: 700,
  },
  disclosure: { color: "#475569", fontSize: 12, marginTop: 8, textAlign: "center" },
  errorText: {
    color: "#b00020",
    fontSize: 12,
    marginTop: 4,
  },
  // Ensure phone number never wraps
  nowrap: {
    whiteSpace: "nowrap",
    wordBreak: "keep-all",
  },
};

export default OptIn;
